/**
 * API client.
 *
 * Every call carries the current Supabase access token. The SDK refreshes it, so
 * we read it per request rather than caching — a cached token is the thing that
 * breaks after an hour on a long studio session.
 *
 * Images normally load straight from their FluidCloud link. The `/api/media/…`
 * fallback route needs the bearer token, so `mediaUrl` fetches that one through
 * here and hands back an object URL.
 */
import { getSupabase } from './supabase';
import { API_BASE } from './env';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function token(): Promise<string | undefined> {
  const supabase = getSupabase();
  if (!supabase) return undefined;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const bearer = await token();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body instanceof FormData ? {} : { 'content-type': 'application/json' }),
      ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const text = await res.text();

  // Not everything that answers us speaks JSON. nginx serves its OWN html for
  // 413 (body over client_max_body_size), 502 and 504, and parsing that gave
  // users `Unexpected token '<', "<!DOCTYPE "...` — which says nothing about
  // what went wrong. Turn those into the sentence they actually mean.
  let body: unknown = {};
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      if (res.status === 413) throw new ApiError(413, 'too_large', 'That file is too large to upload.');
      if (res.status === 504) throw new ApiError(504, 'timeout', 'That took too long and timed out.');
      throw new ApiError(
        res.status,
        'bad_response',
        res.ok ? 'The server sent something unreadable.' : `The server is not responding properly (${res.status}).`,
      );
    }
  }

  if (!res.ok) {
    const e = body as { error?: string; message?: string };
    throw new ApiError(res.status, e.error ?? 'error', e.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) }),
  patch: <T,>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  del: (path: string) => request<void>(path, { method: 'DELETE' }),
  upload: <T,>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form }),
};

/* ----------------------------------------------------------------- types --- */

/** `'auto'` sends no aspect ratio at all — the service follows image 1. */
export type Aspect =
  | 'auto'
  | '1:1' | '1:2' | '2:1' | '1:3' | '3:1' | '2:3' | '3:2' | '3:4' | '4:3'
  | '9:16' | '16:9' | '9:21' | '21:9' | '5:4' | '4:5';
export type Resolution = '1k' | '2k';
export type Format = 'jpeg' | 'png';
export type Category = 'outfits' | 'places' | 'objects' | 'looks';

export interface Photo { id: string; label: string; position: number; src: string | null; ready: boolean }

/**
 * One of the four fixed reference shots, built from the character's own photos
 * against a mannequin base. A row exists while it is still building, so `src`
 * is null until the provider answers.
 */
export interface CharacterReference extends Photo {
  base: string;
  note: string;
  status: 'running' | 'done' | 'failed';
  error: string | null;
  /** Built is not accepted — a set is the character's identity from then on. */
  approved: boolean;
  /** When the build started, for real elapsed time instead of a bare spinner. */
  startedAt: string | null;
}

/** A mannequin base: it fixes the camera and pose, and carries no identity. */
export interface Base {
  id: string; label: string; hint: string; notePlaceholder: string; src: string;
}

export interface Character {
  id: string; name: string; description: string;
  /** The user's own photos of their model — 1 to 9 of them. */
  photos: Photo[];
  /** What renders actually send. */
  references: CharacterReference[];
}
export interface Swatch { id: string; name: string; position: number; src: string; ready: boolean }
export interface LibraryItem {
  id: string; name: string; category: Category; subcategory: string;
  source: 'included' | 'mine'; usedCount: number; swatches: Swatch[];
}
export interface RenderInputRef {
  origin: 'character' | 'library' | 'result'; label: string; role: string; src: string | null;
}
export interface Render {
  id: string; batchId: string; seq: number;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  prompt: string; aspect: Aspect; resolution: Resolution; format: Format;
  inputs: RenderInputRef[]; src: string | null; predictionId: string | null;
  costUsd: number; tookMs: number | null; error: string | null;
  createdAt: string; finishedAt: string | null;
}

/** What the studio sends: references by id, never a URL. */
export type InputSpec =
  | { origin: 'character'; photoId: string; role: string; label: string }
  | { origin: 'library'; swatchId: string; role: string; label: string }
  | { origin: 'result'; renderId: string; role: string; label: string };

export interface StartRenderBody {
  prompt: string; inputs: InputSpec[]; aspect: Aspect;
  resolution: Resolution; format: Format; count: number; characterId?: string;
}

/* ------------------------------------------------------------------ media --- */

const mediaCache = new Map<string, Promise<string>>();
const mediaTypes = new Map<string, string>();

/**
 * Fetch an image and return an object URL. Cached per path for the life of the
 * page — the same swatch appears in the grid, the detail panel, and the inputs
 * strip, and it should be one request.
 *
 * A FluidCloud link is absolute and public: it is fetched WITHOUT the bearer,
 * because an Authorization header on a cross-origin request buys a preflight and
 * nothing else. Only the relative `/api/media/…` fallback needs the token.
 */
export function mediaUrl(path: string): Promise<string> {
  const hit = mediaCache.get(path);
  if (hit) return hit;
  const p = (async () => {
    const bearer = path.startsWith('http') ? undefined : await token();
    const res = await fetch(path, {
      headers: bearer ? { authorization: `Bearer ${bearer}` } : {},
    });
    if (!res.ok) throw new ApiError(res.status, 'media_error', 'Could not load image');
    const blob = await res.blob();
    mediaTypes.set(path, blob.type);
    return URL.createObjectURL(blob);
  })();
  mediaCache.set(path, p);
  return p;
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
};

/**
 * Save an image to disk.
 *
 * A plain `<a download href>` is no good for either source: the media route
 * would 401 without the bearer, and a cross-origin FluidCloud link ignores the
 * `download` attribute. So this downloads the object URL instead.
 * It is deliberately NOT revoked afterwards: it lives in `mediaCache`, and
 * revoking would blank every <img> pointing at the same path.
 *
 * The extension comes off the fetched blob rather than the caller, so an input
 * whose format nobody tracked still lands on disk named correctly.
 */
export async function downloadMedia(path: string, baseName: string): Promise<void> {
  const url = await mediaUrl(path);
  const ext = EXTENSIONS[mediaTypes.get(path) ?? ''] ?? 'jpg';
  const a = document.createElement('a');
  a.href = url;
  a.download = `${baseName}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/* ------------------------------------------------------- her world & days --- */

/**
 * A room, not a photo of her in a room. An anchor carrying her face competes
 * with her four references and the likeness drifts — so a place is empty on
 * purpose, and one room then serves every outfit, hour and beat.
 */
export interface Place {
  id: string; name: string; kind: string; description: string;
  src: string | null; ready: boolean;
  status: 'running' | 'done' | 'failed'; error: string | null;
  position: number; usedCount: number;
  /** Built is not accepted: a beat is only offered a room that was kept. */
  approved: boolean;
  /** Steers one build. Not the description, which travels into every set. */
  note: string;
  /** Photos OF the room. The build composes them into the one image above. */
  photos: Photo[];
}

/** A named slot, not a garment. A set asks for 'sleepwear'; she answers. */
export interface WardrobeSlot {
  id: string; name: string; garment: string;
  swatchId: string | null; src: string | null; position: number;
  /** True when the picture is the slot's own build, not a borrowed swatch. */
  own: boolean;
  status: 'running' | 'done' | 'failed';
  error: string | null;
  /** Built is not accepted: an unkept garment falls back to the swatch. */
  approved: boolean;
  /** Steers one build. Not `garment`, which is what the piece IS. */
  note: string;
  /** Photos OF the garment. The build composes them into the one image above. */
  photos: Photo[];
}

/** Four rungs, per shot, with no ceiling above them. */
export type Tier = 'plain' | 'flirty' | 'suggestive' | 'explicit';

/** 'no-face' is not a crop — it sends no identity at all. */
export type CameraMode = 'selfie' | 'mirror' | 'propped' | 'handheld' | 'no-face';

export interface SetShot {
  id: string; position: number; action: string; outfitState: string;
  tier: Tier; note: string;
  /** How THIS photo is taken. 'no-face' means it carries no identity at all. */
  camera: string;
}

/** One moment: a room, an hour, a light, an outfit slot. Shots happen inside it. */
export interface SetBeat {
  id: string; position: number; name: string;
  placeId: string | null; placeName: string | null;
  camera: string; outfitSlot: string;
  timeLabel: string; dayNight: '' | 'day' | 'night';
  light: string; enabled: boolean; note: string;
  shots: SetShot[];
}

export interface PhotoSet {
  id: string;
  /** Null for a set of nothing but no-face beats — it works for anyone. */
  characterId: string | null; characterName: string | null;
  name: string; description: string; preset: string;
  remixedFrom: string | null; createdAt: string;
  beats: SetBeat[];
  shotCount: number; facelessCount: number; topTier: Tier;
  missingPlaces: number;
}

export interface PresetShotSpec { action: string; outfitState: string; tier: Tier; note: string }
export interface PresetBeatSpec {
  name: string; camera: string; outfitSlot: string; timeLabel: string;
  dayNight: '' | 'day' | 'night'; light: string; placeKind: string; note: string;
  shots: PresetShotSpec[];
}
export interface SetPreset {
  key: string; name: string; description: string;
  anyCharacter: boolean; beats: PresetBeatSpec[];
}

/** One take of a set. A recipe cooked twice has two of these. */
export interface SetRun {
  batchId: string; startedAt: string; count: number; failed: number; costUsd: number;
}

/** A shot's slot in the strip — it exists before the photo does. */
export interface SetShotSlot { id: string; position: number; tier: Tier; camera: string }

/** What a run produced, grouped by the moment it came from. */
export interface DayBeat {
  beatId: string; name: string; timeLabel: string; dayNight: string;
  camera: string; placeName: string | null; outfitSlot: string; light: string;
  shotCount: number;
  shots?: SetShotSlot[];
  renders: {
    id: string; batchId: string; shotId: string | null;
    status: Render['status']; tier: Tier; src: string | null;
    error: string | null; createdAt: string;
  }[];
}

